import { ArrowBackIosNew, ArrowForwardIos, Language } from "@mui/icons-material";
import { Box, Typography, IconButton } from "@mui/material";
import { startOfMonth, startOfWeek, addMonths, isSunday, isSaturday, format, isSameMonth, addDays } from "date-fns";
import { useState, useMemo, useEffect } from "react";

interface CalendarDay {
  date: Date;
  day: string;
  week: string;
  type: "out" | "used" | "free";
}

export function Calendar() {
  let [month, setMonth] = useState(() => {
    return startOfMonth(new Date());
  });

  let [time, setTime] = useState<{ date: Date; time?: string } | null>(null);

  let monthDays = useMemo(() => {
    let firstDayOfTheWeek = startOfWeek(month);
    let nextMonth = addMonths(month, 1);

    let days: CalendarDay[] = [];
    let p = firstDayOfTheWeek;
    do {
      if (isSunday(p) || isSaturday(p)) {
        continue;
      }

      let day = format(p, "d");
      let week = format(p, "EEEEEE");

      days.push({
        date: p,
        day,
        week,
        type: isSameMonth(p, month) ? "free" : "out",
      });
    } while ((p = addDays(p, 1)) && (isSameMonth(p, nextMonth) ? days.length % 5 !== 0 : true));

    // check first row if empty we should delete it
    let isFirstRowShouldBeRemoved = days[4].type === "out";
    if (isFirstRowShouldBeRemoved) {
      days.splice(0, 5);
    }

    return days;
  }, [month]);

  function nextMonth() {
    setMonth(startOfMonth(addMonths(month, 1)));
  }

  function prevMonth() {
    setMonth(startOfMonth(addMonths(month, -1)));
  }

  const formattedDate = (date?: Date) => {
    if (!date) return;
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };
  return (
    <>
      <Box>
        {/* header */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography fontSize={"20px"} fontWeight="600" width="170px">
            {format(month, "MMMM yyyy")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton size="small" sx={{ svg: { fontSize: "18px" } }} onClick={prevMonth}>
              <ArrowBackIosNew />
            </IconButton>
            <IconButton size="small" sx={{ svg: { fontSize: "18px" } }} onClick={nextMonth}>
              <ArrowForwardIos />
            </IconButton>
          </Box>
        </Box>

        {/* body */}
        <Box mt={1} px={{ xs: 0, md: 2 }}>
          <Box sx={{ mb: 2, display: "flex", gap: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            {["M", "T", "W", "TH", "F"].map((day) => (
              <Box key={day} sx={{ flex: 1, py: 2, fontSize: "20px", color: "text.secondary", textAlign: "center" }}>
                {day}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1 }}>
            {monthDays.map((item) => {
              let isUsed = item.type === "used";
              let isFree = item.type === "free";
              let isSelected = formattedDate(time?.date) === formattedDate(item.date);

              return (
                <Box
                  key={item.date.toISOString()}
                  disabled={isUsed}
                  component="button"
                  onClick={() => {
                    if (isFree) {
                      setTime(isSelected ? null : { date: item.date });

                      const dateString = `${item.date.getFullYear()}-${
                        item.date && item.date.getMonth() + 1
                      }-${item.date.getDate()} ${time?.time || "9:00 AM"}`;

                      // updatePickup({ scheduledAt: dateString });
                    }
                  }}
                  sx={[
                    {
                      cursor: "pointer",
                      background: "background.paper",
                      py: 2.5,
                      fontSize: "20px",
                      fontFamily: "Catamaran",
                      textAlign: "center",
                      borderRadius: "4px",
                      border: `1px solid white`,
                    },
                    isUsed && { cursor: "not-allowed", background: "rgba(0, 0, 0, 0.08);" },
                    isFree && { border: (theme) => `1px solid ${theme.palette.primary.main}` },
                    isSelected && { backgroundColor: "primary.main", color: "white" },
                  ]}
                >
                  {item.day}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      {time && (
        <Box mt={5}>
          <Typography fontSize={"20px"} fontWeight="600">
            {format(time.date, "EEEE, MMMM d")}
          </Typography>

          {/* <Typography mt={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Language sx={{ width: "20px", height: "20px" }} />
            Eastern Time (US & Canada) 4:04 pm
          </Typography> */}

          <Box mt={3} sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}>
            {[
              { time: "9:00 AM", type: "free" },
              { time: "11:00 AM", type: "free" },
              { time: "1:00 PM", type: "free" },
              { time: "3:00 PM", type: "free" },
            ].map((item) => {
              let isUsed = item.type === "used";
              let isFree = item.type === "free";
              let isSelected = time?.time === item.time;
              return (
                <Box
                  key={item.time}
                  disabled={isUsed}
                  component="button"
                  onClick={() => {
                    if (isFree) {
                      setTime((t) => t && { ...t, time: item.time });

                      if (time?.date) {
                        const dateString = `${time?.date.getFullYear()}-${
                          time?.date && time?.date.getMonth() + 1
                        }-${time.date.getDate()} ${item.time}`;

                        // updatePickup({ scheduledAt: dateString });
                      }
                    }
                  }}
                  sx={[
                    {
                      cursor: "pointer",
                      background: "background.paper",
                      py: 1,
                      fontSize: "14px",
                      color: "primary.main",
                      fontFamily: "Catamaran",
                      textAlign: "center",
                      borderRadius: "4px",
                      border: (theme) => `1px solid ${theme.palette.primary.main}`,
                    },
                    isUsed && {
                      cursor: "not-allowed",
                      opacity: 0.5,
                      color: "text.secondary",
                      borderColor: "text.secondary",
                    },
                    isSelected && { backgroundColor: "#DDF3FC" },
                  ]}
                >
                  {item.time}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
}
